import React from 'react';
import { TemplateSummary, TemplatesResponse, Channel, FeatureResolution } from 'types/cx-editor';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Box,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  alpha,
  CircularProgress
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import {
  ChevronDown,
  PanelLeftClose,
  PanelRightOpen,
  Info,
  Mail,
  Globe,
  Newspaper,
  MessageSquare,
  Search
} from 'lucide-react';
import { useGlobalConfig } from 'context/GlobalContext';
import { useSelector } from 'react-redux';
import { selectLevel1Data, selectLevel2Data, selectFeatureMetricsLoading, selectFeatureMetricsError } from 'store/dataReducers/featureMetricsSlice';
import { selectProjectAttributes } from 'store/dataReducers/applicationScopeSlice';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { useWidgetSdk } from 'context/WidgetSdkContext';
import { createCxApiClient } from 'framework/http/cxEditorClient';

type Props = {
  selectedTemplateKey?: string;
  onSelect: (templateKey: string) => void;
  isOpen?: boolean;
  onToggleOpen?: () => void;
  disabled?: boolean; // Disable switching templates while preview is rendering
}

// Icon mapping using Record type pattern (matching studio implementation)
const channelIcons: Record<string, React.ElementType> = {
  Email: Mail,
  HER: Newspaper,
  Web: Globe,
  SMS: MessageSquare,
};

const channelIconFor = (name: string) => {
  const Icon = channelIcons[name];
  return Icon ? <Icon size={16} /> : null;
};

// Transform featureId to display name
// Example: "EMAIL.SF.MONTHLY_SUMMARY" -> "Monthly Summary"
const extractTemplateName = (featureId: string): string => {
  // Split by dots and take the last part (e.g., "MONTHLY_SUMMARY")
  const parts = featureId.split('.');
  const lastPart = parts[parts.length - 1] || featureId;
  
  // Convert SNAKE_CASE to Title Case
  // "MONTHLY_SUMMARY" -> "Monthly Summary"
  return lastPart
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Converts bulk-manage API response to TemplateSummary format for Web channel
 * Uses productType as templateKey
 */
const convertBulkManageToWebTemplates = (
  bulkManageData: any[]
): TemplateSummary[] => {
  const templates: TemplateSummary[] = [];
  const templateMap = new Map<string, TemplateSummary>();
  
  // Convert bulk-manage data to templates
  if (Array.isArray(bulkManageData)) {
    bulkManageData.forEach(item => {
      // Use productType as templateKey
      const productType = item?.productType;
      if (!productType) return;
      
      // Use displayName from API or generate from productType
      const displayName = item?.displayName || extractTemplateName(productType);
      const templateKey = productType;
      
      // Only add if it doesn't already exist (avoid duplicates)
      if (!templateMap.has(templateKey)) {
        const template: TemplateSummary = {
          templateKey,
          name: displayName,
          channel: 'Web' as Channel
        };
        templateMap.set(templateKey, template);
        templates.push(template);
      }
    });
  }
  
  // Sort alphabetically by name
  return templates.sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Converts API Level 2 Web templates directly to TemplateSummary format
 * Uses section as templateKey (fallback for when bulk-manage is not available)
 */
const convertApiWebTemplates = (
  apiTemplates: FeatureResolution[]
): TemplateSummary[] => {
  const templates: TemplateSummary[] = [];
  const templateMap = new Map<string, TemplateSummary>();
  
  // Convert API resolutions to templates
  apiTemplates.forEach(apiResolution => {
    const section = apiResolution.section;
    if (!section) return;
    
    // Use section as templateKey and displayName from API or generate from section
    const templateKey = section;
    const displayName = apiResolution.displayName || extractTemplateName(section);
    
    // Only add if it doesn't already exist (avoid duplicates)
    if (!templateMap.has(templateKey)) {
      const template: TemplateSummary = {
        templateKey,
        name: displayName,
        channel: 'Web' as Channel
      };
      templateMap.set(templateKey, template);
      templates.push(template);
    }
  });
  
  // Sort by position if available, otherwise by name
  return templates.sort((a, b) => {
    const aApiTemplate = apiTemplates.find(r => r.section === a.templateKey);
    const bApiTemplate = apiTemplates.find(r => r.section === b.templateKey);
    
    // Sort by position if available
    if (aApiTemplate && bApiTemplate && 
        aApiTemplate.position !== null && bApiTemplate.position !== null) {
      return (aApiTemplate.position || 0) - (bApiTemplate.position || 0);
    }
    if (aApiTemplate && aApiTemplate.position !== null) return -1;
    if (bApiTemplate && bApiTemplate.position !== null) return 1;
    
    // Fallback to alphabetical by name
    return a.name.localeCompare(b.name);
  });
};

// Transform API response to TemplatesResponse format
const transformFeatureResolutionsToTemplates = (
  resolutions: FeatureResolution[], 
  fuelType: string,
  level2Data: FeatureResolution[] | null,
  bulkManageData: any[] | null
): TemplatesResponse => {
  // Safety check: ensure resolutions is an array
  if (!Array.isArray(resolutions)) {
    resolutions = [];
  }
  
  // Filter only ACCESS resolutions (exclude KILL)
  // Also filter by fuelType: include if fuelType matches or if fuelType is not specified in the resolution
  const accessResolutions = resolutions.filter(r => {
    if (!r || r.resolution !== 'ACCESS') {
      return false;
    }
    // If resolution has fuelType, it must match; if not specified, include it
    if (r.fuelType) {
      // Handle fuelType as string, ensuring proper comparison
      const resolutionFuelType = typeof r.fuelType === 'string' 
        ? r.fuelType.trim().toUpperCase() 
        : String(r.fuelType).trim().toUpperCase();
      const projectFuelType = fuelType.trim().toUpperCase();
      return resolutionFuelType === projectFuelType;
    }
    // Include resolutions without fuelType specified (backward compatibility)
    return true;
  });
  
  // Group by product (channel)
  const channelMap = new Map<Channel, TemplateSummary[]>();
  
  // Determine which dashboard productType to look for based on fuelType
  const dashboardProductType = `DASHBOARD.${fuelType.toUpperCase()}`;
  
  // Check Level_1 for Web widgets with matching productType pattern
  // We need to find a match in Level 1 before processing Level 2
  // Check both productType (if exists in Level 1) and featureId for the pattern
  const hasWebWidgetsInLevel1 = accessResolutions.some(r => {
    if (!r || !r.product) return false;
    const product = typeof r.product === 'string' ? r.product.trim().toUpperCase() : '';
    if (product !== 'WEB') return false;
    
    // Check if productType exists and matches the pattern (starts with DASHBOARD.{FUELTYPE})
    if (r.productType) {
      const productTypeUpper = r.productType.toUpperCase();
      if (productTypeUpper.startsWith(dashboardProductType + '.')) {
        return true;
      }
    }
    
    // Fallback: Check featureId for exact match (DASHBOARD.ELECTRIC, DASHBOARD.WATER, DASHBOARD.GAS)
    if (r.featureId) {
      const featureId = r.featureId.toUpperCase();
      if (featureId === dashboardProductType || 
          featureId === 'DASHBOARD.ELECTRIC' || 
          featureId === 'DASHBOARD.WATER' || 
          featureId === 'DASHBOARD.GAS') {
        return true;
      }
    }
    
    return false;
  });
  
  // Process Level_1 data, but SKIP Web products (Web templates come from Level_2)
  accessResolutions.forEach(resolution => {
    // Safety check: skip if resolution is invalid
    if (!resolution || !resolution.featureId) {
      return;
    }
    
    // Handle product field - ensure it's a string and convert to uppercase for comparison
    const productValue = resolution.product;
    if (!productValue || typeof productValue !== 'string') {
      // Skip if product is missing or not a string
      return;
    }
    
    // Map product to Channel type (case-insensitive)
    let channelName: Channel;
    const product = productValue.trim().toUpperCase();
    
    // SKIP Web products - they will be populated from Level_2 data only
    if (product === 'WEB') {
      return;
    }
    
    // SKIP HER products - only WEB should come from Level_2
    if (product === 'HER') {
      return;
    }
    
    if (product === 'EMAIL') {
      channelName = 'Email';
    } else if (product === 'SMS') {
      channelName = 'SMS';
    } else {
      // Skip unknown products instead of defaulting to Email
      // This ensures we only show templates for known channels
      return;
    }
    
    // Extract template key from featureId
    // Example: "EMAIL.SF.MONTHLY_SUMMARY" -> "EMAIL.SF.MONTHLY_SUMMARY"
    const templateKey = resolution.featureId;
    // Use displayName from API if available, otherwise fall back to generated name
    const displayName = resolution.displayName || extractTemplateName(resolution.featureId);
    // Store name without prefix - badge will be shown separately
    
    if (!channelMap.has(channelName)) {
      channelMap.set(channelName, []);
    }
    
    // Check if template already exists (avoid duplicates)
    const existingTemplates = channelMap.get(channelName)!;
    if (!existingTemplates.find(t => t.templateKey === templateKey)) {
      existingTemplates.push({
        templateKey,
        name: displayName,
        channel: channelName
      });
    }
  });
  
  // Special handling for Web channel: use bulk-manage endpoint data
  // Use productType from bulk-manage response as templateKey
  if (bulkManageData && Array.isArray(bulkManageData) && bulkManageData.length > 0) {
    // Convert bulk-manage data to Web templates (using productType as templateKey)
    const webTemplates = convertBulkManageToWebTemplates(bulkManageData);

    // Set templates for Web channel
    if (webTemplates.length > 0) {
      channelMap.set('Web', webTemplates);
    }
  } else if (level2Data && Array.isArray(level2Data)) {
    // Fallback: Use Level 2 data if bulk-manage is not available
    // Fallback: Use Level 2 data if bulk-manage is not available
    // Filter Level_2 data for Web channel with matching productType
    const webLevel2Resolutions = level2Data.filter(r => {
      if (!r || r.resolution !== 'ACCESS') {
        return false;
      }
      
      // Check product is WEB
      const productValue = r.product;
      if (!productValue || typeof productValue !== 'string') {
        return false;
      }
      if (productValue.trim().toUpperCase() !== 'WEB') {
        return false;
      }
      
      // Check productType matches DASHBOARD pattern with fuelType
      if (!r.productType) {
        return false;
      }
      const productTypeUpper = r.productType.toUpperCase();
      const fuelTypeUpper = fuelType.toUpperCase();
      const startsWithDashboard = productTypeUpper.startsWith('DASHBOARD.');
      const containsFuelType = productTypeUpper.includes('.' + fuelTypeUpper + '.') || 
                                productTypeUpper.endsWith('.' + fuelTypeUpper) ||
                                productTypeUpper === 'DASHBOARD.' + fuelTypeUpper;
      const matches = startsWithDashboard && containsFuelType;
      
      if (!matches) {
        return false;
      }
      
      // Check section exists
      if (!r.section) {
        return false;
      }
      
      // Filter by fuelType if specified
      if (r.fuelType) {
        const resolutionFuelType = typeof r.fuelType === 'string' 
          ? r.fuelType.trim().toUpperCase() 
          : String(r.fuelType).trim().toUpperCase();
        const projectFuelType = fuelType.trim().toUpperCase();
        if (resolutionFuelType !== projectFuelType) {
          return false;
        }
      }
      
      return true;
    });

    // Convert API Level 2 data directly to templates (no mock data)
    // Note: This uses section as templateKey (old behavior)
    const webTemplates = convertApiWebTemplates(webLevel2Resolutions);

    // Set merged templates for Web channel
    if (webTemplates.length > 0) {
      channelMap.set('Web', webTemplates);
    }
  }

  // If API data doesn't exist for Web, don't show Web channel
  // No fallback to mock data - only use real API data
  
  // Convert map to array and sort channels in a specific order
  // Note: HER is removed - only WEB comes from Level_2
  const channelOrder: Channel[] = ['Email', 'SMS', 'Web'];
  const channels = channelOrder
    .filter(ch => channelMap.has(ch))
    .map(ch => {
      const templates = channelMap.get(ch);
      return {
        name: ch,
        templates: (templates || []).sort((a, b) => {
          const nameA = a?.name || '';
          const nameB = b?.name || '';
          return nameA.localeCompare(nameB);
        })
      };
    })
    .filter(ch => ch.templates && ch.templates.length > 0); // Only include channels with templates
  
  return { channels };
};

export const TemplatesSidebar: React.FC<Props> = ({ selectedTemplateKey, onSelect, isOpen = true, onToggleOpen, disabled = false }) => {
  const { rc } = useGlobalConfig();
  const level1Data = useSelector(selectLevel1Data);
  const level2Data = useSelector(selectLevel2Data);
  const loading = useSelector(selectFeatureMetricsLoading);
  const error = useSelector(selectFeatureMetricsError);
  const projectAttributes = useSelector(selectProjectAttributes);
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const { isReady: isWidgetSdkReady } = useWidgetSdk();
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const [bulkManageData, setBulkManageData] = React.useState<any[] | null>(null);
  const [bulkManageLoading, setBulkManageLoading] = React.useState(false);
  const [bulkManageError, setBulkManageError] = React.useState<string | null>(null);
  
  // Extract fuelType from projectAttributes (similar to other components)
  const fuelType = React.useMemo(() => {
    let fuelType = 'ELECTRIC'; // Default fallback
    if (projectAttributes?.fuel_type) {
      // Handle different formats: string, array, or comma-separated
      if (typeof projectAttributes.fuel_type === 'string') {
        // If comma-separated, take the first one
        const fuelTypes = projectAttributes.fuel_type.split(',').map(f => f.trim());
        fuelType = fuelTypes[0]?.toUpperCase() || 'ELECTRIC';
      } else if (Array.isArray(projectAttributes.fuel_type)) {
        const fuelTypeArray = projectAttributes.fuel_type as any[];
        if (fuelTypeArray.length > 0) {
          fuelType = String(fuelTypeArray[0]).toUpperCase();
        }
      }
    }
    return fuelType;
  }, [projectAttributes]);
  
  // Fetch bulk-manage data for Web channel templates
  React.useEffect(() => {
    if (!selectedPilotId) {
      setBulkManageData(null);
      return;
    }

    let ignore = false;
    async function fetchBulkManage() {
      setBulkManageLoading(true);
      setBulkManageError(null);
      try {
        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        const pilotIdStr = String(selectedPilotId);
        const response = await api.getBulkManage(pilotIdStr);
        
        if (!ignore) {
          // Response might be an array or an object with an array property
          const data = Array.isArray(response) ? response : (response?.data || response?.items || []);
          setBulkManageData(Array.isArray(data) ? data : []);
        }
      } catch (err: any) {
        if (!ignore) {
          console.error('[TemplatesSidebar] Failed to fetch bulk-manage data:', err);
          setBulkManageError(err.message || 'Failed to load Web templates');
          setBulkManageData(null);
        }
      } finally {
        if (!ignore) {
          setBulkManageLoading(false);
        }
      }
    }

    fetchBulkManage();
    return () => {
      ignore = true;
    };
  }, [selectedPilotId, rc]);

  // Transform Level 1 data to TemplatesResponse format
  const data = React.useMemo(() => {
    // Check if level1Data is null, undefined, or not an array
    if (!level1Data || !Array.isArray(level1Data)) return null;
    return transformFeatureResolutionsToTemplates(level1Data, fuelType, level2Data, bulkManageData);
  }, [level1Data, fuelType, level2Data, bulkManageData]);
  
  // Filter templates based on search query
  const filteredData = React.useMemo(() => {
    if (!data) return null;
    
    if (!searchQuery.trim()) {
      return data;
    }
    
    const query = searchQuery.toLowerCase();
    return {
      ...data,
      channels: (data.channels || [])
        .map(channel => ({
          ...channel,
          templates: (channel.templates || []).filter(template =>
            template && template.name && template.templateKey &&
            (template.name.toLowerCase().includes(query) ||
            template.templateKey.toLowerCase().includes(query))
          )
        }))
        .filter(channel => channel.templates && channel.templates.length > 0)
    };
  }, [data, searchQuery]);

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%', borderRadius: 0, width: '100%', minWidth: isOpen ? 300 : 48, overflow: 'hidden' }}>
      <CardHeader
        sx={{
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
          py: 1.5,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 56
        }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isOpen && (
              <>
                <Typography variant="h6" component="div" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                  CX Templates
                </Typography>
                <Tooltip title="These are the templates currently in scope for the project.">
                  <IconButton size="small" sx={{ p: 0.5 }}>
                    <Info size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </div>
        }
        action={
          onToggleOpen ? (
            <Tooltip title={isOpen ? 'Collapse Templates bar' : 'Expand Templates bar'} placement={isOpen ? 'top' : 'right'}>
              <IconButton size="small" onClick={onToggleOpen} sx={{ ml: 'auto' }}>
                {isOpen ? <PanelLeftClose size={16} /> : <PanelRightOpen size={16} />}
              </IconButton>
            </Tooltip>
          ) : null
        }
      />
      <CardContent sx={{ pt: 1.5, px: 2, pb: 2, flex: 1, display: isOpen ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {error && (
          <Typography variant="body2" color="error" sx={{ py: 2, px: 1 }}>{error}</Typography>
        )}

        {!loading && !error && (
          <>
            <TextField
              placeholder="Search templates"
              size="small"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                  </InputAdornment>
                )
              }}
              sx={{ mb: 1.5 }}
            />

            {((filteredData && Array.isArray(filteredData.channels)) ? filteredData.channels : []).map(ch => {
              const isWebChannel = ch.name === 'Web';
              const isDisabled = isWebChannel && !isWidgetSdkReady;
              
              return (
                <Accordion 
                  key={ch.name}
                  disableGutters 
                  elevation={0} 
                  defaultExpanded={ch.name === 'Email'}
                  disabled={isDisabled}
                  sx={{ 
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': { margin: 0 },
                    opacity: isDisabled ? 0.5 : 1
                  }}
                >
                  <Tooltip 
                    title={isDisabled ? 'Widget SDK is initializing. Web templates will be available once the SDK is ready.' : ''}
                    placement="right"
                  >
                    <AccordionSummary 
                      expandIcon={<ChevronDown size={16} />}
                      sx={{
                        minHeight: 40,
                        '&.Mui-expanded': { minHeight: 40 },
                        px: 1,
                        cursor: isDisabled ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1, 
                          color: isDisabled ? 'text.disabled' : 'text.secondary', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}
                      >
                        {channelIconFor(ch.name)}
                        {ch.name}
                      </Typography>
                    </AccordionSummary>
                  </Tooltip>
                  <AccordionDetails sx={{ pt: 0.5, pb: 0.5, px: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {(ch.templates || []).map((t: TemplateSummary) => {
                      const active = t.templateKey === selectedTemplateKey;
                      const clickDisabled = disabled || (isWebChannel && !isWidgetSdkReady);
                      // Always show EL badge - matching the image design
                      return (
                        <Tooltip key={t.templateKey} title={t.templateKey} placement="right">
                          <Box
                            onClick={() => {
                              if (clickDisabled) return;
                              onSelect(t.templateKey);
                            }}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              gap: 1,
                              width: '100%',
                              px: 1.5,
                              py: 1.5,
                              cursor: clickDisabled ? 'not-allowed' : 'pointer',
                              opacity: clickDisabled ? 0.6 : 1,
                              borderRadius: 1,
                              textAlign: 'left',
                              border: active ? theme => `1px solid ${theme.palette.primary.main}` : '1px solid transparent',
                              backgroundColor: active 
                                ? theme => theme.palette.mode === 'dark' 
                                  ? alpha(theme.palette.primary.main, 0.16) 
                                  : alpha(theme.palette.primary.main, 0.08)
                                : 'transparent',
                              '&:hover': {
                                backgroundColor: (theme) =>
                                  clickDisabled
                                    ? 'transparent'
                                    : theme.palette.mode === 'dark'
                                      ? alpha(theme.palette.common.white, 0.08)
                                      : alpha(theme.palette.common.black, 0.04)
                              },
                              transition: 'all 0.2s ease-in-out'
                            }}
                          >
                            {/* EL Badge - matching EditorToolbar styling */}
                            <Chip 
                              size="small" 
                              variant="outlined" 
                              label="EL"
                              sx={{
                                fontSize: '0.75rem',
                                height: 20,
                                px: 1.5, // px-1.5 equivalent
                                py: 0.5, // py-0.5 equivalent
                                borderColor: theme => alpha(theme.palette.primary.main, 0.5), // border-primary/50 equivalent
                                color: 'primary.main', // text-primary equivalent
                                bgcolor: theme => alpha(theme.palette.primary.main, 0.1), // bg-primary/10 equivalent
                                flexShrink: 0
                              }}
                            />
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis', 
                                whiteSpace: 'nowrap',
                                flex: 1,
                                fontSize: '0.875rem',
                                textAlign: 'left'
                              }}
                            >
                              {t.name}
                            </Typography>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </AccordionDetails>
              </Accordion>
              );
            })}
          </>
        )}
        </div>
      </CardContent>
    </Card>
  );
}
